import { ButtonProtected } from '@components'

import { Button, ButtonsGroup, IconButton, ModalConfirmation, useDrawer, useModal } from '@billy/design-system'
import Cookies from 'js-cookie'
import qs from 'qs'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { AttachmentInformCPAModal } from '@features/attachments/elements/AttachmentInformCPAModal'
import { AttachmentFilterType } from '@features/attachments/enums/attachmentFiltertype'
import { useUploadAttachments } from '@features/attachments/hooks/useUploadAttachments'
import { useIsEInvoicingOnly } from '@features/bills/hooks/useIsEInvoicingOnly'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { ExpensesReceiptUploadSources, ExpensesTrackingEvents } from '@views/receipts/enums/tracking'
import { useReceiptUploadTracking } from '@views/receipts/hooks/useReceiptUploadTracking'

import { CookieKeys } from '../../../../enums/cookieKeys'
import { ModalId } from '../../../../enums/modalId'
import { Scope } from '../../../../enums/scope'
import { TrackingContext } from '../../../../enums/trackingContext'
import { useSegment } from '../../../../hooks'
import {
  BulkNavigationQueryParams,
  defaultQuery as defaultBulkQuery,
} from '../../../../hooks/useBulkNavigationQueryParams'
import { useOrgUsesReceiptsOnly } from '../../../../hooks/useOrgUsesReceiptsOnly'
import { getOrganizationViewUrl } from '../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { ScoutReceiptDeletionReason } from '../../enums/scoutReceiptDeletionReason'
import { useAttachmentsFilters } from '../../hooks/useAttachmentsFilters'
import { useDownloadReceiptsZip } from '../../hooks/useDownloadReceiptsZip'
import { useGetFirstReceipt } from '../../hooks/useGetFirstReceipt'
import { useReceiptsDelete } from '../../hooks/useReceiptsDelete'
import { useScoutAccess } from '../../hooks/useScoutAccess'
import { ReceiptUploadButton } from '../ReceiptUploadButton'
import { ScoutReceiptDeletionConfirmationModal } from '../ScoutReceiptDeletionConfirmationModal'
import * as Styled from './styles'

const DELETE_CONFIRMATION_MODAL_ID = ModalId.DeleteReceiptsConfirmationModal

interface ReceiptsListSideActionsProps {
  hasSelectedScoutReceipts: boolean
  selectedReceiptsIds: string[]
  setSelectedReceiptsIds: (selectedReceiptsIds: string[]) => void
}

export const ReceiptsListSideActions = ({
  hasSelectedScoutReceipts,
  selectedReceiptsIds,
  setSelectedReceiptsIds,
}: ReceiptsListSideActionsProps): ReactElement => {
  const { shouldShowScout } = useScoutAccess()
  const isEInvoicingOnly = useIsEInvoicingOnly()
  const orgUsesReceiptsOnly = useOrgUsesReceiptsOnly()
  const { open: openScoutDrawer } = useDrawer()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const { open: openDeleteConfirmationModal, close: closeDeleteConfirmationModal } =
    useModal(DELETE_CONFIRMATION_MODAL_ID)
  const [{ type, sortBy, sortDirection }] = useAttachmentsFilters()
  const trackReceiptUpload = useReceiptUploadTracking({
    source: ExpensesReceiptUploadSources.UploadFilesButton,
  })
  const { uploadFiles, isUploading } = useUploadAttachments({
    skipAutoForwardInfoModal: orgUsesReceiptsOnly,
    onSuccess: () => {
      trackReceiptUpload()
      track('File Uploaded (Client)', {
        context: TrackingContext.Uploads,
      })
    },
  })
  const { attachment: firstAttachment } = useGetFirstReceipt({ type })
  const { isDeleting, deleteReceipts } = useReceiptsDelete({
    onSuccess: () => {
      setSelectedReceiptsIds([])
      closeDeleteConfirmationModal()
    },
  })
  const { isDownloading, downloadReceiptsZip } = useDownloadReceiptsZip()

  const hasSelectedReceipts = selectedReceiptsIds.length > 0
  const selectedReceiptsCount = selectedReceiptsIds.length
  const showAutomationButton = shouldShowScout

  const handleDeleteButtonClick = useCallback(() => {
    openDeleteConfirmationModal()
  }, [openDeleteConfirmationModal])

  const handleDeleteClick = useCallback(
    (reason?: ScoutReceiptDeletionReason) => {
      deleteReceipts({ receiptsIds: selectedReceiptsIds, reason })
    },
    [deleteReceipts, selectedReceiptsIds],
  )

  const handleDownloadClick = useCallback(() => {
    if (!organization?.id) {
      return
    }

    downloadReceiptsZip({ attachmentIds: selectedReceiptsIds, organizationId: organization.id })
  }, [downloadReceiptsZip, organization?.id, selectedReceiptsIds])

  const handleFillReceipts = useCallback(() => {
    const bulkQuery: BulkNavigationQueryParams = {
      ...defaultBulkQuery,
      bulkSortDirection: sortDirection,
      bulkSortProperty: sortBy,
      bulkUploadType: type,
    }

    if (!organization?.url) {
      return
    }

    track(ExpensesTrackingEvents.AttachmentManageBulkButtonClick, {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      with_selected_receipts: hasSelectedReceipts,
    })

    if (hasSelectedReceipts) {
      Cookies.set(CookieKeys.AttachmentIdsForBulkEdit, qs.stringify(selectedReceiptsIds))

      const bulkEditAttachmentUrl = getOrganizationViewUrl(
        organization?.url,
        reactRoute.organization.bills.getEditBulkAttachmentsRoute(selectedReceiptsIds[0]),
        bulkQuery,
      )

      navigate(bulkEditAttachmentUrl)
      return
    }

    if (firstAttachment) {
      const bulkEditAttachmentUrl = getOrganizationViewUrl(
        organization?.url,
        reactRoute.organization.bills.getEditBulkAttachmentsRoute(firstAttachment.id),
        bulkQuery,
      )

      navigate(bulkEditAttachmentUrl)
    }
  }, [
    firstAttachment,
    hasSelectedReceipts,
    navigate,
    organization?.url,
    selectedReceiptsIds,
    sortDirection,
    sortBy,
    track,
    type,
  ])

  const handleOpenAutomationSidePanelButtonClick = useCallback(() => {
    openScoutDrawer()
    track('Automation Side Panel Opened (Client)', {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      trigger_source: 'MainAutomationButton',
    })
  }, [openScoutDrawer, track])

  return (
    <>
      {isEInvoicingOnly ? (
        <Styled.ReceiptsListSideActionsWrapper>
          <Styled.ButtonsGroupWrapper>
            <ButtonsGroup>
              {selectedReceiptsCount > 0 && (
                <Button color="primary" icon="download" loading={isDownloading} onClick={handleDownloadClick}>
                  {t('download_receipts')}
                </Button>
              )}
            </ButtonsGroup>
          </Styled.ButtonsGroupWrapper>
        </Styled.ReceiptsListSideActionsWrapper>
      ) : (
        <Styled.ReceiptsListSideActionsWrapper>
          <Styled.ButtonsGroupWrapper>
            <ButtonsGroup>
              {selectedReceiptsCount > 0 && !orgUsesReceiptsOnly && (
                <Button color="danger" icon="trash" onClick={handleDeleteButtonClick}>
                  {t('delete_receipts')}
                </Button>
              )}
              <ReceiptUploadButton
                isUploading={isUploading}
                onFilesSelect={uploadFiles}
                trackingContext={TrackingContext.Uploads}
              />
              {!orgUsesReceiptsOnly && (
                <ButtonProtected
                  icon="layers"
                  onClick={handleFillReceipts}
                  disabled={!firstAttachment || type === AttachmentFilterType.Blocked}
                  variant="outlined"
                  scopes={[Scope.BillWrite, Scope.BulkWorkflows]}
                  trackingContext={TrackingContext.Uploads}
                >
                  {hasSelectedReceipts ? t('receipts.fill_in_selected') : t('receipts.fill_in_all')}
                </ButtonProtected>
              )}
              {showAutomationButton && (
                <IconButton
                  aria-label={t('settings.organization.features.email_scanning.title')}
                  icon="zap"
                  id="automation-button"
                  onClick={handleOpenAutomationSidePanelButtonClick}
                  variant="outlined"
                />
              )}
            </ButtonsGroup>
          </Styled.ButtonsGroupWrapper>
        </Styled.ReceiptsListSideActionsWrapper>
      )}

      {hasSelectedScoutReceipts ? (
        <ScoutReceiptDeletionConfirmationModal
          isDeleting={isDeleting}
          onDeleteClick={handleDeleteClick}
          selectedReceiptsCount={selectedReceiptsCount}
        />
      ) : (
        <ModalConfirmation
          cancelLabel={t('cancel')}
          danger
          id={DELETE_CONFIRMATION_MODAL_ID}
          message={t('receipts.delete_modal.message', { count: selectedReceiptsCount })}
          okLabel={t('receipts.delete_modal.ok_label')}
          okLoading={isDeleting}
          onOk={handleDeleteClick}
          title={t('receipts.delete_modal.title', { count: selectedReceiptsCount })}
        />
      )}
      {!orgUsesReceiptsOnly ? <AttachmentInformCPAModal id={ModalId.CpaAttachmentInfoModal} /> : null}
    </>
  )
}
